/**
 * Recovery for stale dynamically-imported chunks after a deployment.
 *
 * Each production build emits content-hashed chunk filenames (e.g.
 * `assets/main-Co2ak8xj.js`) and the game registry lazy-imports those chunks
 * on demand. A new deploy replaces the file set with new hashes, so a tab that
 * loaded the *previous* build still references chunk URLs that no longer exist.
 * When that tab lazily imports a game, the request 404s; the browser then
 * refuses to execute the response as a module ("Expected a JavaScript-or-Wasm
 * module script but the server responded with a MIME type of text/html").
 *
 * The fix is to reload once so the tab picks up the fresh index.html + new
 * hashes. Reloads are bounded per time window so a genuinely broken build
 * degrades to the caller's error UI instead of an infinite reload loop.
 */

const RELOAD_LOG_KEY = "sa:chunk-reloads";
const RELOAD_WINDOW_MS = 60_000;
const MAX_RELOADS_PER_WINDOW = 2;

// Lowercased substrings browsers/Vite use when a dynamic-import chunk is
// missing or served with the wrong MIME type. Matched case-insensitively.
const STALE_CHUNK_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "expected a javascript",
];

/**
 * Detect whether an error is a stale/missing dynamic-import chunk rather than a
 * real runtime error thrown by the module after it loaded.
 */
export function isStaleChunkError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!message) return false;
  const lower = message.toLowerCase();
  return STALE_CHUNK_PATTERNS.some((pattern) => lower.includes(pattern));
}

/**
 * Read the in-window reload timestamps from sessionStorage, dropping any that
 * have aged out of the throttle window.
 */
function recentReloads(): number[] {
  const now = Date.now();
  let log: number[] = [];
  try {
    const raw = sessionStorage.getItem(RELOAD_LOG_KEY);
    if (raw) log = JSON.parse(raw) as number[];
  } catch {
    // Corrupt or unavailable storage — treat as no prior reloads (log stays []).
  }
  return log.filter((ts) => now - ts < RELOAD_WINDOW_MS);
}

/**
 * Reload the page to recover from a stale chunk, unless we've already reloaded
 * the cap within the recent window (in which case we give up so the caller can
 * surface an error instead of looping). Returns true if a reload was triggered.
 */
export function reloadForStaleChunk(): boolean {
  const recent = recentReloads();
  if (recent.length >= MAX_RELOADS_PER_WINDOW) return false;

  recent.push(Date.now());
  try {
    sessionStorage.setItem(RELOAD_LOG_KEY, JSON.stringify(recent));
  } catch {
    // sessionStorage unavailable (private mode / quota). Reload anyway; the
    // cap can't persist, but the user closing the tab still bounds it.
  }
  window.location.reload();
  return true;
}

// ── Active-game gate ───────────────────────────────────────────────────────
// While a game is actively being played, a forced reload would destroy the
// live canvas / match, so we defer it until the game ends (or the tab navigates
// away) and flush it then. Single-player drills surface as sessionPhase
// "playing" too, so this protects them as well. PlayShell owns this signal via
// setGameActive().
let gameActive = false;
let pendingReload = false;

/**
 * Mark whether a game is actively being played. Setting it false flushes any
 * reload that was deferred while the game was live (the player is now back on a
 * safe surface — games list / finished screen). Idempotent.
 */
export function setGameActive(active: boolean): void {
  gameActive = active;
  if (!active && pendingReload) {
    pendingReload = false;
    reloadForStaleChunk();
  }
}

/**
 * Reload now, or — if a game is live — defer until {@link setGameActive}(false).
 * Used by the global deploy-skew handlers so an in-progress game is never
 * reloaded out from under the player. The PlayShell mount-failure path calls
 * {@link reloadForStaleChunk} directly instead (the module never loaded, so
 * there is no live canvas to protect).
 */
function reloadOrDefer(): void {
  if (gameActive) {
    pendingReload = true;
    return;
  }
  reloadForStaleChunk();
}

/**
 * Install the global recovery handler. Vite dispatches `vite:preloadError` on
 * window whenever a lazily-imported chunk fails to load; we swallow the default
 * (which rethrows) and reload once to fetch the new build. This is the primary
 * net for the deploy-skew crash; per-call sites can also call
 * {@link reloadForStaleChunk} directly as a backup.
 */
// Module-level handler references so install is idempotent AND cleanly
// reversible (see _resetForTests). Stored as the exact listener objects passed
// to addEventListener so removeEventListener can match them.
let preloadErrorHandler: EventListener | null = null;
let resourceErrorHandler: EventListener | null = null;

/**
 * Determine whether a failed resource-load `error` event came from one of OUR
 * OWN top-level hashed build assets (a stylesheet or module script under
 * same-origin `/assets/`). Cross-origin URLs and classic scripts are excluded
 * so a failing third-party tag can't trigger a spurious reload.
 */
function isOwnAppShellAssetError(event: Event): boolean {
  const target = event.target as HTMLElement | null;
  if (!target) return false;

  const isStylesheet =
    target.tagName === "LINK" && (target as HTMLLinkElement).rel === "stylesheet";
  // Match only module scripts (and bare <script> with no type, which Vite emits
  // as type="module"). A classic type="text/javascript" tag must NOT count.
  const scriptType = (target as HTMLScriptElement).type;
  const isModuleScript =
    target.tagName === "SCRIPT" && (scriptType === "module" || scriptType === "");
  if (!isStylesheet && !isModuleScript) return false;

  const src = (target as HTMLLinkElement).href || (target as HTMLScriptElement).src || "";
  // The `/assets/` path is also common on third-party CDNs (webpack-hosted
  // libs etc.), so scope to same-origin before matching the prefix — otherwise
  // a failed vendor script triggers a spurious full-page reload.
  // (Origin-scoping + module-type check discovered by greptile.)
  try {
    const u = new URL(src, window.location.href);
    return u.origin === window.location.origin && u.pathname.startsWith("/assets/");
  } catch {
    return false;
  }
}

/**
 * Test-only: remove the installed listeners and reset state so a fresh
 * `installStaleChunkReload()` genuinely re-registers. Without this, the
 * idempotency guard makes every call after the first a silent no-op, which
 * hides setup bugs across test cases (especially under `vi.resetModules()`).
 * Not for production use.
 */
export function _resetForTests(): void {
  gameActive = false;
  pendingReload = false;
  if (preloadErrorHandler) {
    window.removeEventListener("vite:preloadError", preloadErrorHandler);
    preloadErrorHandler = null;
  }
  if (resourceErrorHandler) {
    window.removeEventListener("error", resourceErrorHandler, true);
    resourceErrorHandler = null;
  }
}

export function installStaleChunkReload(): void {
  // Idempotent: guard against double-registration so a single failure can't
  // trigger multiple reload attempts.
  if (preloadErrorHandler || resourceErrorHandler) return;

  preloadErrorHandler = ((event: Event) => {
    event.preventDefault();
    reloadOrDefer();
  }) as EventListener;
  window.addEventListener("vite:preloadError", preloadErrorHandler);

  // Top-level app-shell recovery. `vite:preloadError` only fires for
  // dynamically-imported chunks. The more severe deploy-skew failure is a
  // stale `index.html` whose top-level <link rel="stylesheet"> or
  // <script type="module"> points at a `/assets/*` hash that no longer exists
  // (404 served as text/plain → "Refused to apply style… MIME type"). Those
  // surface as capture-phase `error` events on the element, not as unhandled
  // promise rejections, so we listen for them explicitly and reload once to
  // pick up the fresh HTML + asset hashes.
  resourceErrorHandler = ((event: Event) => {
    if (!isOwnAppShellAssetError(event)) return;
    reloadOrDefer();
  }) as EventListener;
  // Capture phase: resource load errors don't bubble.
  window.addEventListener("error", resourceErrorHandler, true);
}
